import React, { useEffect, useState } from 'react';
import { BookOpen, FileText, Presentation, Code2, Landmark, Zap } from "lucide-react";
import { useDispatch, useSelector } from 'react-redux';
import posthog from 'posthog-js';
import PhoneInput from 'react-phone-input-2'
import { makeStyles } from '@mui/styles';
import es from 'react-phone-input-2/lang/es.json'
import Page from '../../components/Page';
import adminServices from '../../services/adminServices';
import { onBoard } from '../../store/reducer';
import Logo from '../../assets/logo/logo.png';

const iconMap = {
  excel: <BookOpen size={16} />,
  word: <FileText size={16} />,
  powerpoint: <Presentation size={16} />,
  programming: <Code2 size={16} />,
  finance: <Landmark size={16} />,
  lowcode: <Zap size={16} />,
};
export default function LearningJourney({ onComplete, comingfrom, comingJourney }) {
  const dispatch = useDispatch();
  const onboarddetails = useSelector((state) => state?.onboardinfo);
  const [learningTypes, setLearningTypes] = useState([]);
  const [learningTypeCounter, setLearningTypeCounter] = useState(1);
  const [selectedCourseIds, setSelectedCourseIds] = useState([]);
  const [courseCounter, setCourseCounter] = useState(1);
  const [selectedExams, setSelectedExams] = useState([]);
  const [examCounter, setExamCounter] = useState(1);
  const [selectedPaths, setSelectedPaths] = useState([]);
  const [pathCounter, setPathCounter] = useState(1);
  const [courseCategories, setCourseCategories] = useState([]);
  const [assessmentTypeList, setAssessmentTypeList] = useState([]);
  const [batchList, setBatchList] = useState([]);
  const [finishing, setFinishing] = useState(false);
  const [phoneNumber, setPhoneNumber] = useState('');



  useEffect(() => {
    getAssessmentTypeList();
    getCourseCategory()
    getBatchType();
  }, []);

  useEffect(() => {
      if (comingfrom === 'login') {
    setLearningTypes([]);
    setSelectedCourseIds([]);
    setSelectedExams([]);
    setSelectedPaths([]);
    return;
  }
    if (onboarddetails) {
      setLearningTypes(
        (onboarddetails?.learningtypepreference || []).map((item, idx) => ({
          ...item,
          order: item.order ?? idx + 1,
        }))
      );
      setSelectedCourseIds(
        (onboarddetails?.course_learning_path_ids || []).map((item, idx) => ({
          ...item,
          order: item.order ?? idx + 1,
        }))
      );
      setSelectedExams(
        (onboarddetails?.assessment_learning_path_ids || []).map((item, idx) => ({
          ...item,
          order: item.order ?? idx + 1,
        }))
      );
      setSelectedPaths(
        (onboarddetails?.assessment_batch_ids || []).map((item, idx) => ({
          ...item,
          order: item.order ?? idx + 1,
        }))
      );
    }
  }, []);

  const getAssessmentTypeList = async () => {
    try {
      const result = await adminServices.getAssessmentTypeDropdown();
      if (result?.ok) {
        const list = result.data?.data || result.data || [];
        setAssessmentTypeList(list.filter(item => item.is_active === true && item.enable_user === true));
      }
    } catch {
      setAssessmentTypeList([]);
    }
  };


  const getCourseCategory = async () => {
    try {
      const res = await adminServices.getB2CCategoryList();
      if (res.ok && res.data?.data) {
        setCourseCategories(res.data.data.filter(item => item.is_active === true));
      }
    } catch (err) {
      console.error('Failed to load B2C categories:', err);
    }
  };


  const getBatchType = async () => {
    try {
      const result = await adminServices.getBatchType();
      if (result?.ok) {
        const list = result.data?.data || result.data || [];
        setBatchList(list);
      }
    } catch {
      setBatchList([]);
    }
  };

  const showCourse = learningTypes.some(i => i.type === 'course');
  const showAssessment = learningTypes.some(i => i.type === 'assessment');

  const orderedSections = [...learningTypes]
    .sort((a, b) => a.order - b.order)
    .map(i => i.type);

  const showLearningPath = assessmentTypeList.some(
    item => selectedExams.some(e => e.id === item.id) && item.allow_learning_path === true
  );

  const isLearningTypeSelected = type => learningTypes.some(i => i.type === type);
  const isCourseSelected = id => selectedCourseIds.some(i => i.id === id);
  const isExamSelected = id => selectedExams.some(i => i.id === id);
  const isPathSelected = id => selectedPaths.some(i => i.id === id);
  const toggleLearningType = (type) => {
    setLearningTypes(prev => {
      const exists = prev.find(i => i.type === type);
      if (exists) {
        if (type === 'course') { setSelectedCourseIds([]); }
        if (type === 'assessment') { setSelectedExams([]); setSelectedPaths([]); }
        return prev.filter(i => i.type !== type);
      }
      return [...prev, { type, order: prev.length + 1 }];
    });
  };

  const toggleCourse = (id) => {
    setSelectedCourseIds(prev => {
      const exists = prev.find(i => i.id === id);
      if (exists) return prev.filter(i => i.id !== id);
      return [...prev, { id, order: prev.length + 1 }];
    });
  };

  const toggleExam = (id) => {
    setSelectedExams(prev => {
      const exists = prev.find(i => i.id === id);
      if (exists) return prev.filter(i => i.id !== id);
      return [...prev, { id, order: prev.length + 1 }];
    });
  };

  const togglePath = (id) => {
    setSelectedPaths(prev => {
      const alreadySelected = prev.find(i => i.id === id);
      if (alreadySelected) return [];
      return [{ id, order: 1 }];
    });
  };


  const handleFinish = async () => {
    setFinishing(true);
    try {
      const sortByOrder = arr => [...arr].sort((a, b) => a.order - b.order);
      const payload = {
        learningTypePreference: sortByOrder(learningTypes).map(item => ({
          type: item.type,
          selectionOrder: item.order,
        })),
        courseLearningPathIds: sortByOrder(selectedCourseIds).map(item => ({
          id: item.id,
          selectionOrder: item.order,
        })),
        assessmentLearningPathIds: sortByOrder(selectedExams).map(item => ({
          id: item.id,
          selectionOrder: item.order,
        })),
        assessmentBatchIds: sortByOrder(selectedPaths).map(item => ({
          id: item.id,
          selectionOrder: item.order,
        })),
        phoneNumber,
        comingfrom

      };
      const result = await adminServices.useronBoard(payload);
      dispatch(onBoard(result.data.result));
      posthog.capture('onboarding_completed', {
        learning_types: learningTypes.map(i => i.type),
        course_categories_count: selectedCourseIds.length,
        exam_categories_count: selectedExams.length,
      });
      setPhoneNumber('')
      if(comingfrom !== "profile"){
        
      setSelectedCourseIds([])
      setLearningTypes([])
      setSelectedPaths([])
      setSelectedExams([])
      }
    } catch (err) {
      console.error('Onboarding save failed:', err);
    } finally {
      setFinishing(false);
      if (comingfrom === "login" || comingfrom === "profileskip") {
        onComplete();
      }

    }
  };

  const handleSkip = () => onComplete();
  const canFinish = () => {
    if (learningTypes.length === 0) return false;

    if (showCourse && showAssessment) {
      if (selectedCourseIds.length === 0 && selectedExams.length === 0) return false;
    } else {
      if (showCourse && selectedCourseIds.length === 0) return false;
      if (showAssessment && selectedExams.length === 0) return false;
    }

    if (showAssessment && showLearningPath && selectedPaths.length === 0) return false;

    return true;
  };
  let buttonLabel = 'Finish & Launch →';
  if (finishing) {
    buttonLabel = 'Saving...';
  } else if (comingfrom === 'profile') {
    buttonLabel = 'Save SetUp Preferences';
  }
  else if (comingfrom === 'profileskip') {
    buttonLabel = 'Finish & Launch →';
  }
  return (
    // <Page title="">
    <div className={`w-full ${comingfrom === 'profile' || comingfrom === 'profileskip' ? 'pb-8 pt-0' : 'flex justify-center p-4 py-8'}`}>
      <div className={`w-full bg-white ${comingfrom === 'profile' || comingfrom === 'profileskip' ? '' : 'max-w-md lg:max-w-4xl rounded-3xl border border-slate-200 shadow-sm'}`}>

        {comingfrom === 'login' && (
          <div className="flex justify-between items-center px-6 py-5 border-b">
            <button
              className="bg-transparent border-0 p-0"
              disabled
              aria-label="Navigate to dashboard">
              <img
                id='logoKSS'
                src={Logo}
                alt="Logo"
                className="h-10 sm:h-12 w-auto object-contain"
              />
            </button>
            {/* <div className="text-xl font-semibold">
                <span className="text-teal-500">key</span>
                <span className="text-orange-400">Skillset</span>
              </div> */}
            {/* <button
              onClick={handleSkip}
              disabled={finishing}
              className="text-slate-500 text-sm disabled:opacity-50"
            >
              → Skip
            </button> */}
          </div>)}

        {(comingJourney === 'google' && comingfrom !== 'profile') &&
          <div className="p-5 lg:p-8">

            <div className="border rounded-2xl p-5">
              <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-3">
                <div>
                  <h2 className="text-xl sm:text-2xl font-bold">Prefered Contact Details</h2>

                </div>

              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-6">
                <PhoneInput country={'in'}
                  localization={es}
                  id='outlined-basic'
                  value={phoneNumber}
                  onChange={((value) => setPhoneNumber(value))}

                  inputProps={{ name: "phone", placeholder: "" }}
                  inputClass="phone-input"

                  containerStyle={{
                    width: "100%",
                    marginTop: "5px",
                    borderRadius: "30px",
                    boxShadow: "rgba(99, 99, 99, 0.2) 0px 2px 8px 0px",
                    background: "#fff",
                  }}

                  inputStyle={{
                    width: "100%",
                    height: "43px",
                    borderRadius: "30px",
                    paddingLeft: "52px",
                    border: "transparent",
                    boxShadow: "none",
                    fontSize: "0.85rem",
                    background: "transparent",
                  }}

                  buttonStyle={{
                    borderRadius: "30px 0 0 30px",
                    border: "none",
                    boxShadow: "none",
                    background: "transparent",
                  }}

                  buttonClass="phone-flag-btn"

                  dropdownStyle={{
                    zIndex: 2000,
                  }}
                />
              </div>


            </div>
          </div>
        }







        <div className="p-5 lg:p-8">
          {/* <div className={`px-5 pb-5 lg:px-8 lg:pb-8 ${comingfrom === 'profile' || comingfrom === 'profileskip' ? 'pt-0' : 'pt-5 lg:pt-8'}`}> */}
          {comingfrom === 'login' && (<div className="border rounded-xl p-4 mb-6">
            <h3 className="text-xs font-bold uppercase tracking-wider text-blue-600">
              Set Up Your Learning Journey
            </h3>
            <p className="text-sm text-slate-500 mt-2">
              Choose your preferred learning format, exam category and academic level to personalize your study experience.
            </p>
          </div>)}

          <div className="border rounded-2xl p-5">
            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-3">
              <div>
                <h2 className="text-xl sm:text-2xl font-bold">How do you prefer learning?</h2>
                <p className="text-slate-500 text-sm mt-2">
                  Select all media formats that fit your workspace style.
                </p>
              </div>
              {comingfrom === 'profileskip' && (
                <a className="flex items-center gap-1.5 text-sm text-blue-500 font-medium mt-1 shrink-0">
                  <span className="w-4 h-4 rounded-full border border-blue-500 flex items-center justify-center text-[10px] shrink-0">i</span>
                  <span className="whitespace-normal sm:whitespace-nowrap">Complete Your Onboarding Journey!</span>
                </a>
              )}
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-6">
              {['assessment'].map(type => {
                const selected = isLearningTypeSelected(type);
                const orderVal = learningTypes.find(i => i.type === type)?.order;
                const checkboxClass = selected
                  ? 'bg-teal-500'
                  : 'border border-slate-300 bg-white';
                const cardClass = selected
                  ? 'border-teal-500 bg-teal-50'
                  : 'border-slate-200';
                const desc = type === 'course'
                  ? 'Syllabus courses and video structures'
                  : 'Skill evaluations and tests';
                return (
                  <button
                    key={type}
                    onClick={() => toggleLearningType(type)}
                    className={`relative text-left border rounded-xl p-4 transition-all ${cardClass}`}
                  >
                    {selected && (
                      <span className="absolute right-9 top-3 text-[10px] text-teal-600 font-bold">
                        #{orderVal}
                      </span>
                    )}
                    <div className={`absolute right-3 top-3 w-5 h-5 rounded flex items-center justify-center text-white text-xs ${checkboxClass}`}>
                      {selected && '✓'}
                    </div>
                    <h4 className="font-semibold capitalize">{type === 'assessment' ? 'Test Series' : type}</h4>
                    <p className="text-xs text-slate-500 mt-2">{desc}</p>
                  </button>
                );
              })}
            </div>

            {orderedSections.map(type => (
              <div key={type}>

                {type === 'course' && (
                  <div className="mt-8">
                    <h3 className="text-xl font-bold text-blue-600">Choose Course Categories</h3>
                    <p className="text-sm text-slate-500 mt-1">
                      Select the specific subject categories to enable in your workspace.
                    </p>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mt-5">
                      {courseCategories.map(course => {
                        const selected = isCourseSelected(course.id);
                        const orderVal = selectedCourseIds.find(i => i.id === course.id)?.order;

                        const cardClass = selected
                          ? 'border-blue-400 bg-blue-50'
                          : 'border-slate-200 hover:bg-slate-50';

                        return (
                          <button
                            key={course.id}
                            onClick={() => toggleCourse(course.id)}
                            className={`relative border rounded-xl p-4 text-left transition ${cardClass}`}
                          >
                            {/* {selected && (
                                <span className="absolute top-2 right-2 bg-blue-500 text-white text-[10px] font-bold rounded-full w-4 h-4 flex items-center justify-center">
                                  {orderVal}
                                </span>
                              )} */}
                            <div className="flex items-center gap-2 mb-2 text-slate-600">
                              {iconMap[course.icon] || <BookOpen size={16} />}
                            </div>
                            <span className="text-sm font-medium">
                              {course.description}
                            </span>

                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}

                {type === 'assessment' && (
                  <div className="mt-8">
                    <h3 className="text-xl font-bold text-blue-600">Choose Test Series Categories</h3>
                    <p className="text-sm text-slate-500 mt-1">
                      Select standard high-school entrance prep exams and board tests.
                    </p>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-2 gap-y-3 md:gap-3 mt-5 items-stretch">
                      {assessmentTypeList.map(exam => {
                        const selected = isExamSelected(exam.id);
                        const orderVal = selectedExams.find(i => i.id === exam.id)?.order;

                        const cardClass = selected
                          ? 'border-blue-500 bg-blue-50 text-blue-600'
                          : 'border-slate-200';

                        return (
                          <button
                            key={exam.id}
                            onClick={() => toggleExam(exam.id)}
                            style={{
                              height: 'auto',
                              minHeight: '56px',
                              overflow: 'visible',
                              whiteSpace: 'normal',
                              wordBreak: 'break-word',
                            }}
                            className={`relative border rounded-xl p-3 md:p-4 md:h-full flex flex-col items-center justify-center gap-1 md:gap-2 text-center transition ${cardClass}`}
                          >
                            <span className="text-xs md:text-sm font-medium leading-snug">
                              {exam.code}
                            </span>
                          </button>
                        );
                      })}
                    </div>
                    {showLearningPath && (
                      <div className="mt-8">
                        <h3 className="text-xl font-bold text-blue-600">Pick Your Learning Path</h3>
                        <p className="text-sm text-slate-500 mt-1">Select your class or category.</p>
                        <div className="flex flex-wrap gap-3 mt-5">
                          {batchList.map(path => {
                            const selected = isPathSelected(path.id);
                            const orderVal = selectedPaths.find(i => i.id === path.id)?.order;
                            const btnClass = selected
                              ? 'bg-blue-500 text-white border-blue-500'
                              : 'border-slate-200 hover:bg-slate-50';
                            return (
                              <button
                                key={path.id}
                                onClick={() => togglePath(path.id)}
                                className={`relative px-5 py-2 rounded-lg border transition ${btnClass}`}
                              >
                                {/* {selected && (
                                    <span className="absolute -top-1.5 -right-1.5 bg-white border border-blue-500 text-blue-600 text-[10px] font-bold rounded-full w-4 h-4 flex items-center justify-center leading-none">
                                      {orderVal}
                                    </span>
                                  )} */}
                                {path.name}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                )}

              </div>
            ))}

          </div>
        </div>
        <div className="flex justify-end p-5 border-t bg-white sticky bottom-0">
          <button
            onClick={handleFinish}
            disabled={(finishing || !canFinish())}
            className="bg-blue-500 hover:bg-blue-600 disabled:opacity-60 disabled:cursor-not-allowed text-white px-6 py-3 rounded-xl font-medium flex items-center gap-2 transition"
          >
            {/* {finishing ? 'Saving...' : 'Finish & Launch →'} */}
            {buttonLabel}
          </button>
        </div>

      </div>
    </div>
    // </Page>
  );
}